import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/ds/components/avatar";
import { getUserFullName, getUserInitials } from "../lib/lib";
import { TCommentBase } from "../model/types/types";
import { formatRelativeTime } from "@/zapi-frontend/utils/dates";
import { CommentActions } from "./_CommentActions";
import { useState } from "react";
import { ReplyEditInputField } from "./_ReplyEditInputField";

type TReplyButtonProps = {
  commentReplies: TCommentBase[];
  isReplyOpen: boolean;
  userId: number;
  onDelete: (id: string) => void;
  parentId: string;
};

export function CommentReplies(props: TReplyButtonProps) {
  const { commentReplies, isReplyOpen, userId, parentId, onDelete } = props;

  const { t } = useTranslation("projectHome");
  const [isEditReply, setIsEditReply] = useState<string | null>(null);

  if (!isReplyOpen)
    return (
      <span className="typo-caption flex w-fit items-center gap-1.5 text-muted-foreground transition-colors group-hover/comment:text-foreground">
        <span className="flex -space-x-1">
          {commentReplies.slice(0, 3).map((reply) => (
            <Avatar key={reply.id} className="size-5 ring-2 ring-card">
              <AvatarFallback className="text-[9px]">{getUserInitials(reply.author)}</AvatarFallback>
            </Avatar>
          ))}
        </span>
        {t("comments.replyCount", { count: commentReplies.length })}
      </span>
    );

  return (
    <div className="flex flex-col gap-3 border-s border-foreground/10 ps-3">
      {commentReplies.map((reply) => (
        <div key={reply.id} className="flex flex-col gap-1" data-testid={`reply-comment-${reply.id}`}>
          <div className="flex items-center gap-2">
            <Avatar className="size-6">
              <AvatarFallback className="typo-caption">{getUserInitials(reply.author)}</AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
              <span className="typo-label-sm truncate text-foreground">{getUserFullName(reply.author)}</span>
              <span className="typo-caption shrink-0 text-muted-foreground">{formatRelativeTime(reply.createdAt)}</span>
            </div>
            <CommentActions
              commentId={reply.id}
              onDelete={onDelete}
              onEdit={() => setIsEditReply(reply.id)}
              isCanEdit={reply.author.id === userId}
              isVisible={isReplyOpen}
            />
          </div>
          <div className="ms-8">
            {isEditReply === reply.id ? (
              <ReplyEditInputField
                userId={userId}
                commentId={reply.id}
                commentContent={reply.content}
                isEditField
                parentId={parentId}
                onSubmit={() => setIsEditReply(null)}
                onCancel={() => setIsEditReply(null)}
              />
            ) : (
              <p className="typo-body-sm whitespace-pre-wrap text-foreground">{reply.content}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
